import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, TrendingUp, FileText, Users, Building2, DollarSign, Sparkles, Bell, BarChart3, Shield } from "lucide-react";
import { InstitutionSettings } from "@/hooks/useInstitutionSettings";

interface FeatureModulesProps {
  settings: InstitutionSettings;
}

const FEATURE_MODULES = [
  {
    id: "enable_deal_pipeline",
    name: "Deal Pipeline Dashboard",
    description: "Visual Kanban-style pipeline for tracking deals from prospect to completion",
    icon: TrendingUp,
    recommendedFor: ["power5", "fcs", "div2", "div3"],
  },
  {
    id: "enable_contract_versioning",
    name: "Contract Versioning",
    description: "Track contract versions with AI-powered change detection",
    icon: FileText,
    recommendedFor: ["power5", "fcs", "div2"],
  },
  {
    id: "enable_agent_marketplace",
    name: "Agent Marketplace",
    description: "Verified agent directory with compliance ratings",
    icon: Users,
    recommendedFor: ["div2", "div3", "naia"],
  },
  {
    id: "enable_brand_marketplace",
    name: "Brand Partner Marketplace",
    description: "Connect athletes with verified brand opportunities",
    icon: Building2,
    recommendedFor: ["div2", "div3", "naia", "highschool"],
  },
  {
    id: "enable_monetization_hub",
    name: "Athlete Monetization Hub",
    description: "AI-matched deal opportunities and market rate calculator for athletes",
    icon: DollarSign,
    recommendedFor: ["div2", "div3", "naia", "highschool"],
  },
  {
    id: "enable_conference_partnership",
    name: "Conference Partnership",
    description: "Shared rule library and multi-institution management",
    icon: Sparkles,
    recommendedFor: ["fcs", "div2", "div3", "naia"],
  },
  {
    id: "enable_roi_tracking",
    name: "Compliance ROI Dashboard",
    description: "Track violations prevented, time saved, and revenue enabled",
    icon: BarChart3,
    recommendedFor: ["power5", "fcs", "div2", "div3"],
  },
  {
    id: "enable_real_time_alerts",
    name: "Real-Time Rule Alerts",
    description: "Email notifications for state and conference rule changes",
    icon: Bell,
    recommendedFor: ["power5", "fcs", "div2", "div3"],
  },
  {
    id: "enable_compliance_audit_tool",
    name: "Free Compliance Audit Tool",
    description: "Lead generation tool for analyzing compliance gaps",
    icon: Shield,
    recommendedFor: ["power5", "fcs"],
  },
];

const MARKET_SEGMENTS = [
  { value: "power5", label: "Power 5 / FBS" },
  { value: "fcs", label: "FCS / Div I-AA" },
  { value: "div2", label: "Division II" },
  { value: "div3", label: "Division III" },
  { value: "naia", label: "NAIA" },
  { value: "highschool", label: "High School" },
];

export const FeatureModules = ({ settings }: FeatureModulesProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [marketSegment, setMarketSegment] = useState(settings.market_segment || "power5");
  const [features, setFeatures] = useState<Record<string, boolean>>({
    enable_deal_pipeline: settings.enable_deal_pipeline ?? true,
    enable_contract_versioning: settings.enable_contract_versioning ?? true,
    enable_agent_marketplace: settings.enable_agent_marketplace ?? false,
    enable_brand_marketplace: settings.enable_brand_marketplace ?? false,
    enable_monetization_hub: settings.enable_monetization_hub ?? false,
    enable_conference_partnership: settings.enable_conference_partnership ?? false,
    enable_roi_tracking: settings.enable_roi_tracking ?? true,
    enable_real_time_alerts: settings.enable_real_time_alerts ?? true,
    enable_compliance_audit_tool: settings.enable_compliance_audit_tool ?? false,
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("institution_settings" as any)
        .update({
          market_segment: marketSegment,
          ...features,
        } as any)
        .eq("id", settings.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Feature modules updated successfully",
      });

      queryClient.invalidateQueries({ queryKey: ["institution-settings"] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleFeature = (featureId: string) => {
    setFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId],
    }));
  };

  return (
    <Card className="border-2 hover:border-primary/20 transition-colors">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle>Feature Modules</CardTitle>
        </div>
        <CardDescription>
          Enable optional features based on your institution's needs and tier
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Market Segment Selector */}
        <div className="space-y-2">
          <Label htmlFor="market_segment">Market Segment</Label>
          <Select 
            value={marketSegment} 
            onValueChange={(value) => setMarketSegment(value as typeof marketSegment)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select your market segment" />
            </SelectTrigger>
            <SelectContent>
              {MARKET_SEGMENTS.map(segment => (
                <SelectItem key={segment.value} value={segment.value}>
                  {segment.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Your market segment helps us recommend appropriate features
          </p>
        </div>

        {/* Feature Toggles */}
        <div className="space-y-4">
          <h3 className="font-semibold text-sm">Available Modules</h3>
          {FEATURE_MODULES.map(module => {
            const Icon = module.icon;
            const isRecommended = module.recommendedFor.includes(marketSegment);
            
            return (
              <div
                key={module.id}
                className="flex items-start justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start gap-3 flex-1">
                  <Icon className="h-5 w-5 text-primary mt-0.5" />
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={module.id} className="text-base font-medium cursor-pointer">
                        {module.name}
                      </Label>
                      {isRecommended && (
                        <Badge variant="outline" className="text-xs">
                          Recommended
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {module.description}
                    </p>
                  </div>
                </div>
                <Switch
                  id={module.id}
                  checked={features[module.id] ?? false}
                  onCheckedChange={() => toggleFeature(module.id)}
                />
              </div>
            );
          })}
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Feature Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
